"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.JilModelList = void 0;
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const get_1 = __importDefault(require("lodash/get"));

const has_1 = __importDefault(require("lodash/has"));

const ModelList_1 = require("./ModelList");

const types_1 = require("./types");

class JilModelList extends ModelList_1.ModelList {
  constructor(options = {}) {
    var _a;

    super(options);
    this.SEARCH_QUERY_PATTERN = new RegExp(/^[^*]*\*?$/);
    this.filter = {
      lastQuery: '',
      query: options.filterQuery || '',
      visible: false
    };
    this.pagination = {
      currentPage: options.pageNumber || 1,
      itemCount: options.itemCount || parseInt((_a = options.headers) === null || _a === void 0 ? void 0 : _a.get(types_1.JIL_HEADERS.TOTAL_COUNT), 10) || 0,
      pageSize: options.pageSize || types_1.MODEL_LIST_CONSTANTS.LIST_DEFAULT_PAGE_SIZE
    };
    this.sort = {
      expression: options.sortExpression || '',
      order: options.sortOrder || ''
    };
  }
  /**
   * @description Method to fetch the total count of items for this list.
   *
   * @return {Number} the number of items available to this list on the server
   */


  getTotalItemCount() {
    return get_1.default(this, 'pagination.itemCount');
  }
  /**
   * @description Method to determine if there are any items in the list.  If this is a filtered
   *              list and there are no items we do not know whether any items would exist in an
   *              unfiltered list.
   *
   * @return {Boolean|undefined} true, false or undefined if filter may be hiding items
   */


  hasContent() {
    if (!this.isEmpty()) {
      return true;
    }

    return this.filter.query.length > 0 ? undefined : false;
  }
  /**
   * @description Method to determine if there are any items in this list on the server.
   *
   * @return {Boolean} true if the number of items available to this list on the server is 0
   */


  isEmpty() {
    return get_1.default(this, 'pagination.itemCount', 0) === 0;
  }
  /**
   * @description Method to reset the list to its collapsed state.
   *   This is being introduced for tables with a large number of items that wish to return to a
   *   search view after each operation rather than refreshing the entire list.
   *
   // eslint-disable-next-line valid-jsdoc
    * returns {Promise} a promise which is resolved with the model
    */


  reset() {
    throw new Error(`${this.constructor.name}.reset() method unimplemented`);
  }
  /**
   * @description Method to set the current page of results to display.
   *
   * @param {Number} zeroBasedNewPage 0-based number to select
   */


  setPage(zeroBasedNewPage) {
    if (zeroBasedNewPage > -1 && zeroBasedNewPage !== this.pagination.currentPage - 1) {
      // valid page (starts at zero and not same page that is currently selected)
      this.pagination.currentPage = 1 + zeroBasedNewPage;
    }
  }
  /**
   * @description Method to set the current page of results to display.
   *
   * @param [{Number}] newPage 1-based page number to select.
   *   Default is 1 which is the first page.
   */


  setPageNumber(newPage = 1) {
    if (newPage >= 1) {
      this.pagination.currentPage = newPage;
    }
  }
  /**
   * @description Method to set the page size of current results to display.
   *
   * @param {Number} newPageSize number of results to display per page
   */


  setPageSize(newPageSize) {
    if (newPageSize > 0) {
      // valid size for page
      this.pagination.pageSize = newPageSize;
    }
  }
  /**
   * @description Method to determine whether we need to update the total itemCount of a list.
   *
   * @return {Boolean} true if the total itemCount needs to be updated
   */


  shouldUpdateTotalItemCount() {
    return has_1.default(this, 'pagination.itemCount') && this.filter.query === '';
  }
  /**
   * @description Method to change the sort ordering for this list.
   * @param {String} property - attribute to sort people by
   * @param {Boolean} [desc] - true if should sort in descending order, else false
   */


  sortBy(property, desc) {
    this.sort = {
      expression: property,
      order: desc ? types_1.SortOrder.DESC : types_1.SortOrder.ASC
    };
  }

}

exports.JilModelList = JilModelList;